import React, { useState } from 'react';
import {
  Box, IconButton, Badge, Popover, Typography, List, ListItemButton,
  ListItemText, Divider, Button, Chip, CircularProgress, Snackbar, Alert,
} from '@mui/material';
import {
  NotificationsOutlined as BellIcon,
  Task as TaskIcon,
  Info as InfoIcon,
  Mail as InvitationIcon,
  Warning as AlertIcon,
  Celebration as WelcomeIcon,
  Campaign as SystemIcon,
  DoneAll as ReadAllIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { platformNotificationApi, PlatformNotification, NotificationType } from 'shared/services/platformNotificationApi';
import { queryKeys } from 'shared/hooks/queryConfig';
import { useAuth } from 'shared/contexts/AuthContext';
import { UserRole } from 'shared/types';
import { rewriteLegacyActionUrl } from 'shared/utils/notificationUrl';

const timeAgo = (dateStr: string): string => {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'ahora';
  if (diffMin < 60) return `hace ${diffMin} min`;
  if (diffHr < 24) return `hace ${diffHr}h`;
  if (diffDay < 7) return `hace ${diffDay}d`;
  return date.toLocaleDateString('es-CR', { day: '2-digit', month: 'short' });
};

const getTypeIcon = (type: NotificationType) => {
  switch (type) {
    case 'task': return <TaskIcon sx={{ color: 'info.main' }} />;
    case 'info': return <InfoIcon sx={{ color: '#9E9E9E' }} />;
    case 'invitation': return <InvitationIcon sx={{ color: '#9C27B0' }} />;
    case 'alert': return <AlertIcon sx={{ color: 'warning.main' }} />;
    case 'welcome': return <WelcomeIcon sx={{ color: 'success.main' }} />;
    case 'system': return <SystemIcon sx={{ color: 'primary.main' }} />;
    default: return <InfoIcon />;
  }
};

export const NotificationBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { hasRole, user } = useAuth();
  const open = Boolean(anchorEl);
  // Scope notification queries to the active user so impersonation /
  // logout-login on the same tab can't leak counts across identities.
  const userId = user?._id || '';

  // Poll unread count every 60s
  const { data: unreadCount = 0 } = useQuery({
    queryKey: queryKeys.notifications.unreadCount(userId),
    queryFn: () => platformNotificationApi.getUnreadCount(),
    refetchInterval: 60_000,
    enabled: !!userId,
  });

  // Fetch notification list on-demand when popover opens
  const {
    data: notifications = [],
    isFetching: loading,
    refetch: refetchNotifications,
  } = useQuery({
    queryKey: queryKeys.notifications.list({ limit: 10, isArchived: false }, userId),
    queryFn: async () => {
      const res = await platformNotificationApi.getAll({ limit: 10, isArchived: false });
      return res.data?.notifications || [];
    },
    enabled: false,
  });

  // Mark single notification as read
  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => platformNotificationApi.markAsRead(id),
    onSuccess: (_data, id) => {
      qc.setQueryData<number>(queryKeys.notifications.unreadCount(userId), (prev) =>
        Math.max(0, (prev ?? 1) - 1),
      );
      qc.setQueryData<PlatformNotification[]>(
        queryKeys.notifications.list({ limit: 10, isArchived: false }, userId),
        (prev) => prev?.map((n) => (n._id === id ? { ...n, isRead: true } : n)),
      );
    },
    onError: (err: any) => {
      setActionError(err?.message || 'No se pudo marcar la notificación como leída');
    },
  });

  // Mark all as read
  const markAllAsReadMutation = useMutation({
    mutationFn: () => platformNotificationApi.markAllAsRead(),
    onSuccess: () => {
      qc.setQueryData<number>(queryKeys.notifications.unreadCount(userId), 0);
      qc.setQueryData<PlatformNotification[]>(
        queryKeys.notifications.list({ limit: 10, isArchived: false }, userId),
        (prev) => prev?.map((n) => ({ ...n, isRead: true })),
      );
    },
    onError: (err: any) => {
      setActionError(err?.message || 'No se pudieron marcar todas como leídas');
    },
  });

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    refetchNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickNotification = (notification: PlatformNotification) => {
    if (!notification.isRead) {
      markAsReadMutation.mutate(notification._id);
    }
    // 1) Rewrite legacy actionUrls (pre-route-rename rows still in the DB).
    let url = rewriteLegacyActionUrl(notification.actionUrl || '/notificaciones');

    // 2) Non-platform users only have /my-company/* routes for their own
    // company. If a notification was generated with an admin-style
    // /companies/:id/... URL, rewrite it so the user actually lands
    // somewhere they can read.
    const isAdminCompanyUrl = /^\/companies\/[^/]+(\/|$)/.test(url);
    const isPlatformUser = hasRole([UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF]);
    if (isAdminCompanyUrl && !isPlatformUser) {
      const rest = url.replace(/^\/companies\/[^/]+/, '');
      url = rest ? `/my-company${rest}` : '/my-company';
    }

    navigate(url);
    handleClose();
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const handleViewAll = () => {
    navigate('/notificaciones');
    handleClose();
  };

  return (
    <>
      <IconButton onClick={handleOpen} size="small" sx={{ color: 'text.secondary' }}>
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <BellIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { width: 380, maxHeight: 480, overflow: 'hidden', display: 'flex', flexDirection: 'column' },
          },
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>Notificaciones</Typography>
            {unreadCount > 0 && (
              <Chip label={unreadCount} size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
            )}
          </Box>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<ReadAllIcon sx={{ fontSize: 16 }} />}
              onClick={handleMarkAllAsRead}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              Marcar todo como leído
            </Button>
          )}
        </Box>
        <Divider />

        {/* Notification list */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">No hay notificaciones</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {notifications.map((notification) => (
                <ListItemButton
                  key={notification._id}
                  onClick={() => handleClickNotification(notification)}
                  sx={{
                    borderLeft: notification.isRead ? 'none' : '2px solid',
                    borderLeftColor: notification.isRead ? 'transparent' : 'primary.main',
                    bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                    py: 1.5,
                    px: 2,
                    '&:hover': { bgcolor: 'action.selected' },
                  }}
                >
                  <Box sx={{ mr: 1.5, mt: 0.25 }}>
                    {getTypeIcon(notification.type)}
                  </Box>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        fontWeight={notification.isRead ? 400 : 600}
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 }}
                      >
                        {notification.title.length > 50
                          ? `${notification.title.substring(0, 50)}...`
                          : notification.title}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 280 }}>
                          {notification.body.length > 80
                            ? `${notification.body.substring(0, 80)}...`
                            : notification.body}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.disabled" sx={{ mt: 0.25 }}>
                          {timeAgo(notification.createdAt)}
                        </Typography>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>

        {/* Footer */}
        <Divider />
        <Box sx={{ textAlign: 'center', py: 1 }}>
          <Button size="small" onClick={handleViewAll} sx={{ textTransform: 'none' }}>
            Ver todo
          </Button>
        </Box>
      </Popover>

      <Snackbar
        open={!!actionError}
        autoHideDuration={5000}
        onClose={() => setActionError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      </Snackbar>
    </>
  );
};
